import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Filter from "@Components/Filters/Filter";
import { transformFiltersToQuery } from "@Components/Filters/filterUtills";
import { Filters } from "@Components/Filters/types";
import LoadingFullScreen from "@Components/Loading/LoadingFullScreen";
import { Deal } from "@Types/deal";
import { LenderTicket } from "@Types/tickets";
import { TitleIssue } from "@Types/title_issues";
import { User } from "@Types/users";
import { Dealership } from "@Types/dealerships";
import { Role } from "@Types/roles";
import { Team } from "@Types/teams";
import Pagination from "./Pagination";
import Sort from "./Sort";
import { transformSort } from "./Sort/Sort";
import SortBy from "./Sort/types";
import styles from "./Table.module.scss";
import { TableProps } from "./types";
import { useStickyState } from "@Hooks/useStickyState";

export function truncate(
  input: string | undefined,
  length: number
): string | undefined {
  if (input && input?.length > length + 3) {
    return input.substring(0, length) + "...";
  }
  return input;
}

const Table = <
  T extends User | Deal | TitleIssue | LenderTicket | Role | Team | Dealership
>({
  id,
  query,
  columns,
  entityType,
  disabledRow,
  noEntriesMessage = "No entries found!",
  useGetFunction,
  outerItemsPerPage = 10,
  queryParam = "page",
}: TableProps<T>) => {
  const [filters, setFilters] = useState<Filters | undefined>(undefined);
  const [sort, setSort] = useState<SortBy | undefined>();
  const [hoverRow, setHoverRow] = useState<number | undefined>();

  const navigate = useNavigate();
  const [itemsPerPage, setItemsPerPage] = useStickyState<number>(
    outerItemsPerPage,
    id ?? entityType
  );

  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get(queryParam)) || 1;

  const filtersAreOn = filters
    ? Object.keys(transformFiltersToQuery(filters)).length > 0
    : false;

  const {
    data: entries,
    isLoading,
    totalDocs,
    totalPages,
  } = useGetFunction({
    query: {
      ...(query ? query : {}),
      ...(filters ? transformFiltersToQuery(filters) : {}),
    },
    options: {
      limit: filtersAreOn ? undefined : itemsPerPage,
      offset: !filtersAreOn ? (currentPage - 1) * itemsPerPage : 0,
      ...(sort ? transformSort(sort) : { sort: { createdAt: "desc" } }),
    },
  });

  return (
    <div className={styles.tableContainer}>
      <table
        cellSpacing={0}
        cellPadding={0}
        border={0}
        className={styles.table}
      >
        <thead className={styles.tableHead}>
          <tr>
            {columns.map((column, index) => (
              <th key={index} className={styles.columnHeader}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    gap: "5px",
                  }}
                >
                  {column.label}
                  {column.sortPath && (
                    <Sort
                      sort={sort}
                      path={column.sortPath}
                      setSort={setSort}
                    />
                  )}
                  {column?.filters && (
                    <Filter
                      id={column.id}
                      columnFilters={column.filters}
                      filterState={filters?.[column.id]}
                      setFilterState={(newFilterState) => {
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          [column.id]: newFilterState,
                        }));
                      }}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries?.length ? (
            entries.map((entry, index) => {
              return (
                <tr
                  key={index}
                  className={styles.row}
                  onMouseEnter={() => {
                    setHoverRow(index);
                  }}
                  onMouseLeave={() => {
                    setHoverRow(undefined);
                  }}
                  onClick={() =>
                    disabledRow?.(entry)
                      ? null
                      : "_id" in entry &&
                        entityType &&
                        location.pathname !== `/${entityType}/${entry._id}` &&
                        navigate(
                          `/${entityType}/${entry._id}?${queryParam}=${currentPage}`
                        )
                  }
                >
                  {columns.map((column, columnIndex) => {
                    const style = column?.style ? column?.style(entry) : {};
                    const value = column.value(entry);
                    return (
                      <td key={columnIndex} className={styles.column}>
                        <div className={styles.columnNameMobile}>
                          {column.label}
                        </div>
                        <div
                          className={styles.columnContainer}
                          style={
                            column?.id === "actions"
                              ? { ...style, paddingLeft: "15px" }
                              : column?.id === "delete"
                              ? { ...style, paddingLeft: "10px" }
                              : style
                          }
                        >
                          {column.hoverIcon && index === hoverRow
                            ? column.hoverIcon
                            : column.icon}
                          <span>
                            {" "}
                            {column.truncate && typeof value === "string"
                              ? truncate(value, column.truncate)
                              : column.value(entry)}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : isLoading ? (
            <tr className={styles.notFound}>
              <td colSpan={columns.length ?? 1}>
                <LoadingFullScreen />
              </td>
            </tr>
          ) : (
            <tr className={styles.notFound}>
              <td colSpan={columns.length ?? 1}>{noEntriesMessage}</td>
            </tr>
          )}
        </tbody>
      </table>

      {entries && entries.length > 0 && !filtersAreOn && (
        <Pagination
          queryParam={queryParam}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalDocs={totalDocs}
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

export default Table;
